import React from 'react';
import {Link} from 'react-router-dom';
import {AlertTriangleIcon, MailIcon} from '../Icons';
import {secondaryButtonClass, Spinner} from '../formStyles';

interface SignupCheckInboxProps {
    email: string;
    /** The link has been sent, so a resend is a request the server will accept. */
    linkSent: boolean;
    /** Long enough with no link that the mail is worth chasing rather than waiting on. */
    stalled: boolean;
    resending: boolean;
    /** Seconds left on the one-send-per-minute throttle. 0 when a resend is allowed. */
    cooldownSeconds: number;
    resendError: string | null;
    /** True after a successful resend, so the button can confirm it did something. */
    resent: boolean;
    onResend: () => void;
}

/**
 * The last screen of signup: the card went through, so go and open your email.
 *
 * This is shown the moment Paddle reports a completed checkout, **not** when provisioning finishes.
 * The account is usually created within a second or two, but that is a webhook race the visitor has
 * no part in and would not wait out — so the copy is written to be true either way, and nothing on
 * the screen moves while the account catches up.
 *
 * Clicking the emailed link is what proves the address belongs to whoever paid, so there is no
 * session to hand back here. The password comes after.
 */
const SignupCheckInbox: React.FC<SignupCheckInboxProps> = ({
  email,
  linkSent,
  stalled,
  resending,
  cooldownSeconds,
  resendError,
  resent,
  onResend,
}) => (
  <>
    <div className="mt-5 flex items-start gap-2 rounded-lg border border-emerald-500/30
     bg-emerald-500/[0.06] px-3 py-2.5 text-sm text-emerald-200">
      <MailIcon className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
                We have emailed <span className="font-medium">{email}</span> a one-time sign-in link.
                Clicking it is what confirms the address is yours.
      </span>
    </div>

    <p className="mt-3 text-xs text-gray-500">
            You will pick a password as soon as you are in, and sign in with it from then on.
    </p>

    {resendError && (
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/30
       bg-red-500/[0.06] px-3 py-2.5 text-xs text-red-200">
        <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{resendError}</span>
      </div>
    )}

    {linkSent && (
      <button
        type="button"
        onClick={onResend}
        disabled={resending || cooldownSeconds > 0}
        className={`mt-4 w-full ${secondaryButtonClass}`}
      >
        {resending && <Spinner />}
        {resending
          ? 'Sending…'
          : cooldownSeconds > 0
            ? `Resend in ${cooldownSeconds}s`
            : resent ? 'Send it again' : 'Didn’t get it? Resend the link'}
      </button>
    )}

    {/* Only for the rare signup our side loses: with no link sent there is nothing to resend, so
        without this the screen would offer no way forward at all. */}
    {!linkSent && stalled && (
      <p className="mt-4 text-xs text-gray-500">
                Nothing in your inbox after a few minutes?{' '}
        <Link to="/contact" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                    Contact support
        </Link>
        {' '}with the address above and we will sort it out — your payment went through, so do not pay again.
      </p>
    )}
  </>
);

export default SignupCheckInbox;
